/**
 * The text the AI answers callers from - and the ONLY text it answers from.
 *
 * Its own card with its own save, because it is a different kind of thing from
 * the settings above it: a document the owner edits over days, which should
 * not be saved by somebody flipping a switch, nor lost by somebody saving one.
 *
 * The handbook (Juhised) is never read to callers. It is written for staff -
 * margins, debt handling, internal routines - and not one of its guides was
 * safe to say to a customer as written (docs/crm-calls-ai-uuring.md section 6).
 * "Load draft" asks the server for a prepared starting text, puts it in the
 * box and saves nothing: the notes that come with it are what the owner has to
 * check before it becomes something a machine says on the phone.
 */
import { useEffect, useState, type FormEvent } from "react";
import { BookOpen, FileDown, RefreshCw, RotateCcw, Save } from "lucide-react";
import { useT } from "../../../../../i18n/I18nProvider";
import {
  AGENT_KNOWLEDGE_MAX,
  getAgentKnowledge,
  getAgentKnowledgeDraft,
  knowledgeTooLongMax,
  saveAgentKnowledge,
  type AgentKnowledge,
} from "../../../../../lib/telephonyAgent";
import { Btn, StatusPill, fmtDateTime, fmtNum } from "../../../../ui";
import { ConfirmModal } from "../../../_shared/ConfirmModal";
import { IntegrationCard } from "../../panel/IntegrationCard";
import { Field, Note, SaveRow } from "../../panel/fields";
import { estimateTokens } from "./aiAgentNotes";

export function KnowledgeEditor() {
  const t = useT();
  const [saved, setSaved] = useState<AgentKnowledge | null>(null);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  /** The server's ceiling after it refused the text as too long. */
  const [rejectedMax, setRejectedMax] = useState<number | null>(null);
  const [draftState, setDraftState] = useState<"idle" | "loading" | "error">("idle");
  const [draftNotes, setDraftNotes] = useState<string[] | null>(null);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    getAgentKnowledge()
      .then((k) => {
        setSaved(k);
        setText(k.text);
      })
      .catch(() => setLoadFailed(true))
      .finally(() => setLoading(false));
  }, []);

  const dirty = text !== (saved?.text ?? "");
  const chars = text.length;
  const overLimit = chars > AGENT_KNOWLEDGE_MAX;
  // The server's own figure for the text it holds, and the same formula for
  // anything that is not saved yet.
  const tokens = saved && !dirty ? saved.approxTokens : estimateTokens(chars);

  async function loadDraft() {
    setConfirming(false);
    setDraftState("loading");
    try {
      const draft = await getAgentKnowledgeDraft();
      setText(draft.text);
      setDraftNotes(draft.notes);
      setDraftState("idle");
    } catch {
      setDraftState("error");
    }
  }

  function askForDraft() {
    // Typed work is the one thing this button must never replace silently.
    // The saved text needs no question: it is still on the server, one
    // "discard changes" away.
    if (dirty && text.trim()) setConfirming(true);
    else void loadDraft();
  }

  function discard() {
    setText(saved?.text ?? "");
    setDraftNotes(null);
    setRejectedMax(null);
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setSaveState("saving");
    setRejectedMax(null);
    try {
      const next = await saveAgentKnowledge(text);
      setSaved(next);
      setText(next.text);
      // Whatever the draft's notes asked for has now been decided on.
      setDraftNotes(null);
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 2500);
    } catch (err) {
      setRejectedMax(knowledgeTooLongMax(err));
      setSaveState("error");
    }
  }

  // The server resolves the name. Without one the line gives the date alone:
  // a raw user id beside it says nothing a reader can use.
  const who = saved?.savedByName?.trim() || null;
  const meta = saved?.savedAt
    ? who
      ? t("settings.aiAgent.knowledge.savedAtBy", { when: fmtDateTime(saved.savedAt), who })
      : t("settings.aiAgent.knowledge.savedAt", { when: fmtDateTime(saved.savedAt) })
    : t("settings.aiAgent.knowledge.neverSaved");

  return (
    <IntegrationCard
      icon={BookOpen}
      title={t("settings.aiAgent.knowledge.title")}
      desc={t("settings.aiAgent.knowledge.desc")}
      status={dirty && !loading ? <StatusPill tone="wt">{t("settings.aiAgent.knowledge.unsaved")}</StatusPill> : null}
    >
      <form onSubmit={handleSave} className="space-y-4">
        <Note tone="info">{t("settings.aiAgent.knowledge.about")}</Note>
        {loadFailed ? <Note tone="bad">{t("settings.aiAgent.knowledge.loadError")}</Note> : null}
        {draftNotes ? (
          <Note tone="warn">
            <span className="block font-[650]">{t("settings.aiAgent.knowledge.draftLoaded")}</span>
            {/* `list-disc` spelled out: preflight strips list markers, and a
                list of things to check that reads as one paragraph gets
                checked as one. */}
            {draftNotes.length > 0 ? (
              <ul className="mt-1.5 list-disc space-y-1 pl-4">
                {draftNotes.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            ) : null}
          </Note>
        ) : null}
        {draftState === "error" ? <Note tone="bad">{t("settings.aiAgent.knowledge.draftError")}</Note> : null}

        <Field
          label={t("settings.aiAgent.knowledge.label")}
          hint={
            <span className="flex flex-wrap gap-x-3 gap-y-0.5">
              <span className={overLimit ? "text-[var(--color-destructive)]" : undefined}>
                {t("settings.aiAgent.knowledge.count", { chars: fmtNum(chars), max: fmtNum(AGENT_KNOWLEDGE_MAX) })}
              </span>
              <span>{t("settings.aiAgent.knowledge.tokens", { n: tokens, tokens: fmtNum(tokens) })}</span>
              <span>{meta}</span>
            </span>
          }
        >
          <textarea
            className="crm-area min-h-[320px]"
            rows={16}
            value={text}
            disabled={loading}
            placeholder={t("settings.aiAgent.knowledge.placeholder")}
            onChange={(e) => setText(e.target.value)}
          />
        </Field>

        {overLimit ? (
          <Note tone="bad">{t("settings.aiAgent.knowledge.tooLongLocal", { max: fmtNum(AGENT_KNOWLEDGE_MAX) })}</Note>
        ) : null}
        {rejectedMax !== null ? (
          <Note tone="bad">{t("settings.aiAgent.knowledge.tooLong", { max: fmtNum(rejectedMax) })}</Note>
        ) : null}
        {!loading && !text.trim() ? <Note tone="mut">{t("settings.aiAgent.knowledge.empty")}</Note> : null}

        <SaveRow>
          {/* Locked while the saved text is unknown: a save typed over a text
              that never loaded would overwrite what the AI is saying now. */}
          <Btn type="submit" tone="dark" disabled={saveState === "saving" || overLimit || loading || loadFailed}>
            {saveState === "saving" ? <RefreshCw className="size-3.5 animate-spin" /> : <Save className="size-3.5" />}
            {saveState === "saving" ? t("settings.aiAgent.knowledge.saving") : t("settings.aiAgent.knowledge.save")}
          </Btn>
          <Btn onClick={askForDraft} disabled={draftState === "loading" || loading}>
            {draftState === "loading" ? (
              <RefreshCw className="size-3.5 animate-spin" />
            ) : (
              <FileDown className="size-3.5" />
            )}
            {draftState === "loading"
              ? t("settings.aiAgent.knowledge.loadingDraft")
              : t("settings.aiAgent.knowledge.loadDraft")}
          </Btn>
          {dirty && !loading ? (
            <Btn onClick={discard}>
              <RotateCcw className="size-3.5" />
              {t("settings.aiAgent.knowledge.revert")}
            </Btn>
          ) : null}
          {saveState === "saved" ? <StatusPill tone="ok">{t("settings.aiAgent.knowledge.saved")}</StatusPill> : null}
          {saveState === "error" && rejectedMax === null ? (
            <StatusPill tone="lt">{t("settings.aiAgent.knowledge.saveError")}</StatusPill>
          ) : null}
        </SaveRow>
      </form>

      {confirming ? (
        <ConfirmModal
          title={t("settings.aiAgent.knowledge.replaceTitle")}
          body={t("settings.aiAgent.knowledge.replaceBody")}
          confirmLabel={t("settings.aiAgent.knowledge.replaceConfirm")}
          cancelLabel={t("settings.aiAgent.knowledge.cancel")}
          // Not red: nothing is lost that "discard changes" cannot bring back
          // from the server, so this is not the delete dialog's question.
          tone="dark"
          onConfirm={loadDraft}
          onClose={() => setConfirming(false)}
        />
      ) : null}
    </IntegrationCard>
  );
}
